import React, { useMemo, useState } from 'react';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameDay,
  isSameMonth,
  addMonths,
  subMonths,
} from 'date-fns';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { ResourceBookingSchema } from '../../data/types';
import {
  findBookingsForDay,
  formatBookingTimeRange,
  capitalizeWords,
  getBookingStatusColor,
} from '../../utils/helpers';
import moment from 'moment-timezone';

type BookingsCalendarViewProps = {
  bookings: ResourceBookingSchema[];
  onViewDetails?: (booking: ResourceBookingSchema) => void;
};

const BookingsCalendarView: React.FC<BookingsCalendarViewProps> = ({
  bookings,
  onViewDetails,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  const daysInMonth = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentMonth));
    const end = endOfWeek(endOfMonth(currentMonth));
    return eachDayOfInterval({ start, end });
  }, [currentMonth]);

  const bookingsForSelectedDay = useMemo(() => {
    if (!selectedDay) return [];
    return findBookingsForDay(moment(selectedDay), bookings);
  }, [selectedDay, bookings]);

  const getDayBookings = (day: Date) => findBookingsForDay(moment(day), bookings);

  return (
    <div className="w-full px-4">
      <div className="flex flex-row items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => setCurrentMonth((m) => subMonths(m, 1))}
          className="flex items-center justify-center w-9 h-9 rounded-full border border-[#E9E9E9] text-[#013847]"
        >
          <ArrowBackIosIcon fontSize="small" />
        </button>
        <h2 className="font-raleway font-semibold text-base text-[#013847]">
          {format(currentMonth, 'MMMM yyyy')}
        </h2>
        <button
          type="button"
          onClick={() => setCurrentMonth((m) => addMonths(m, 1))}
          className="flex items-center justify-center w-9 h-9 rounded-full border border-[#E9E9E9] text-[#013847]"
        >
          <ArrowForwardIosIcon fontSize="small" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
          <div
            key={`${day}-${i}`}
            className="text-center text-[10px] font-raleway font-semibold text-[#8A8A8A] py-1"
          >
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {daysInMonth.map((day) => {
          const dayBookings = getDayBookings(day);
          const isSelected = selectedDay && isSameDay(day, selectedDay);
          const inMonth = isSameMonth(day, currentMonth);
          const isToday = isSameDay(day, new Date());

          return (
            <button
              key={day.toISOString()}
              type="button"
              onClick={() => setSelectedDay(day)}
              className={`min-h-[56px] p-0.5 border rounded-md text-left ${
                isSelected ? 'border-[#013847] bg-[#e5ebec]' : 'border-[#E9E9E9]'
              } ${!inMonth ? 'opacity-40' : ''}`}
            >
              <span
                className={`inline-flex items-center justify-center w-5 h-5 text-[10px] font-raleway rounded-full ml-0.5 ${
                  isToday ? 'bg-[#013847] text-white font-bold' : 'text-[#013847]'
                }`}
              >
                {format(day, 'd')}
              </span>
              {dayBookings.length > 0 && (
                <div className="flex justify-center gap-0.5 mt-0.5">
                  {dayBookings.slice(0, 3).map((b) => {
                    const color = getBookingStatusColor(b.status || 'pending');
                    return (
                      <span
                        key={b.id}
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: color.primaryTextColor }}
                      />
                    );
                  })}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {selectedDay && (
        <div className="mt-4 border border-[#E9E9E9] rounded-xl p-3 bg-[#FAFAFA]">
          <h3 className="font-raleway font-semibold text-sm text-[#013847] mb-2">
            {format(selectedDay, 'EEEE, MMM d')}
            <span className="text-[#8A8A8A] font-normal ml-1">
              ({bookingsForSelectedDay.length})
            </span>
          </h3>

          {bookingsForSelectedDay.length === 0 ? (
            <p className="text-xs font-raleway text-[#8A8A8A]">No bookings on this day.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {bookingsForSelectedDay.map((booking) => {
                const color = getBookingStatusColor(booking.status || 'pending');
                return (
                  <button
                    key={booking.id}
                    type="button"
                    onClick={() => onViewDetails?.(booking)}
                    className="flex flex-col p-3 bg-white border border-[#E9E9E9] rounded-lg text-left w-full"
                  >
                    <div className="flex flex-row items-center justify-between gap-2">
                      <p className="font-raleway font-semibold text-sm text-[#013847]">
                        {booking.resource_name}
                      </p>
                      {booking.status && (
                        <span
                          className="px-2 py-0.5 rounded text-[10px] font-raleway font-bold capitalize"
                          style={{
                            backgroundColor: color.bgColor,
                            color: color.primaryTextColor,
                          }}
                        >
                          {capitalizeWords(booking.status.replace(/_/g, ' '))}
                        </span>
                      )}
                    </div>
                    <p className="text-xs font-raleway text-[#8A8A8A] mt-1">
                      {formatBookingTimeRange(booking)}
                      {booking.resource_type && ` · ${booking.resource_type}`}
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BookingsCalendarView;
